import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../services/api";
import { listCompanies, type Company } from "../../services/companies-api";
import {
  bulkUploadQuestions,
  createQuestion,
  deactivateQuestion,
  listQuestions,
  reactivateQuestion,
  removeQuestion,
  updateQuestion,
  type LanguageTag,
  type Question,
  type QuestionInput,
  type QuestionUploadResult,
} from "../../services/questions-api";
import { QuestionForm } from "./QuestionForm";

/** FR-001/FR-002/FR-025/FR-027: browse/filter the bank, and create/edit/deactivate questions. */
export function QuestionBankPage() {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [appFilter, setAppFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState<LanguageTag | "">("");
  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">("");
  const [kindFilter, setKindFilter] = useState<"" | "feedback" | "scored">("");
  const [editing, setEditing] = useState<Question | null | "new">(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<QuestionUploadResult | null>(null);
  const [uploading, setUploading] = useState(false);

  async function refresh() {
    const results = await listQuestions({
      app: appFilter || undefined,
      systemId: companyFilter || undefined,
      language: languageFilter || undefined,
      // FR-073: no implicit "active" filter. Deactivated questions used to vanish from the only
      // screen that lists questions, which left no way to bring one back — the whole reason the
      // Deactivate column below is a toggle rather than a one-way action.
      status: statusFilter || undefined,
    });
    setQuestions(results);
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appFilter, companyFilter, languageFilter, statusFilter]);

  useEffect(() => {
    listCompanies()
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, []);

  async function handleSubmit(input: QuestionInput) {
    setSubmitting(true);
    try {
      if (editing && editing !== "new") {
        await updateQuestion(editing.id, input);
      } else {
        await createQuestion(input);
      }
      setEditing(null);
      await refresh();
    } finally {
      setSubmitting(false);
    }
  }

  /** FR-046: bulk-create from a CSV. Every row that lands becomes an ordinary question — the
   * list below is the same list, with no separate "bulk" state to leave. */
  async function handleBulkUpload(file: File) {
    setUploading(true);
    setError(null);
    try {
      const result = await bulkUploadQuestions(file);
      setUploadResult(result);
      await refresh();
    } catch {
      setError("Could not read that file. It needs to be a CSV with the template's columns.");
    } finally {
      setUploading(false);
    }
  }

  /** FR-073: the Deactivate column's toggle. Switching it on retires the question from future
   * assignments; switching it back off returns it to circulation. */
  async function handleToggleActive(question: Question) {
    setBusyId(question.id);
    setError(null);
    try {
      if (question.status === "active") await deactivateQuestion(question.id);
      else await reactivateQuestion(question.id);
      await refresh();
    } finally {
      setBusyId(null);
    }
  }

  /** FR-073: hard delete, as opposed to deactivation. The backend refuses with 409 once the
   * question has gone out in an assignment, so that past results keep pointing at something. */
  async function handleRemove(question: Question) {
    if (!window.confirm(`Remove this question permanently?

${question.text}`)) return;
    setBusyId(question.id);
    setError(null);
    try {
      await removeQuestion(question.id);
      await refresh();
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
      setError(body?.message ?? "Could not remove this question.");
    } finally {
      setBusyId(null);
    }
  }

  // FR-056: "show me only my feedback questions" is the common reason to come here.
  const visibleQuestions = questions.filter((q) =>
    kindFilter === "feedback" ? q.isFeedback : kindFilter === "scored" ? !q.isFeedback : true,
  );

  if (editing) {
    return (
      <div className="p-6">
        <button onClick={() => setEditing(null)} className="mb-4 text-sm text-brand">
          ← Back to question bank
        </button>
        <h1 className="mb-4 font-sans text-xl text-ink">
          {editing === "new"
            ? t("questionBank.createTitle", "Create Question")
            : t("questionBank.editTitle", "Edit Question")}
        </h1>
        <QuestionForm
          initial={editing === "new" ? undefined : editing}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-sans text-xl text-ink">{t("questionBank.title", "Question Bank")}</h1>
        <div className="flex items-center gap-3">
          {/* FR-046: the template carries the dropdowns and the app catalogue, so downloading it
              is the intended starting point rather than typing headers from scratch. */}
          <a
            href="/templates/question-upload-template.xlsx"
            download
            className="rounded px-2 py-2 font-sans text-sm text-brand underline"
          >
            Download template
          </a>
          <label className="cursor-pointer rounded bg-surface-alt px-4 py-2 font-sans text-sm text-ink">
            {uploading ? "Uploading…" : "Bulk upload CSV"}
            <input
              type="file"
              accept=".csv,text/csv"
              aria-label="Question CSV file"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleBulkUpload(file);
                // Reset, so re-uploading the same corrected file still fires onChange.
                e.target.value = "";
              }}
            />
          </label>
          <button
            onClick={() => setEditing("new")}
            className="rounded bg-brand px-4 py-2 font-sans text-surface"
          >
            {t("questionBank.newQuestion", "+ New question")}
          </button>
        </div>
      </div>

      <p className="mb-4 text-sm text-body">
        CSV columns: <code>Language, Question Text, Type, Option 1-5, Correct Answer(s),
        Explanation, Tags, Visibility, Company, Feedback, Position Types</code>. Correct Answer(s)
        accepts either the option number (<code>1</code>, or <code>1, 3</code> for multi-select) or
        the option text. Company is the sign-in slug, and is only needed when Visibility is{" "}
        <em>Company specific</em>. Set <code>Feedback</code> to TRUE for an unscored feedback
        question (it then needs no Tags and no correct answer).
      </p>

      {uploadResult && (
        <div className="mb-4 rounded border border-surface-alt bg-surface-alt px-3 py-2 text-sm">
          <p className="text-ink">Created {uploadResult.created} question(s).</p>
          {uploadResult.rejectedRows.length > 0 && (
            <>
              <p className="mt-1 text-accent">
                Rejected {uploadResult.rejectedRows.length} row(s) — the rest were still created:
              </p>
              <ul className="mt-1 list-inside list-disc text-accent">
                {uploadResult.rejectedRows.map((r) => (
                  <li key={r.row}>
                    Row {r.row}: {r.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="mb-4 flex gap-3">
        <input
          value={appFilter}
          onChange={(e) => setAppFilter(e.target.value)}
          placeholder="Filter by app tag"
          className="rounded border border-surface-alt px-3 py-2 text-sm"
        />
        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          className="rounded border border-surface-alt px-3 py-2 text-sm"
        >
          <option value="">All companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={languageFilter}
          onChange={(e) => setLanguageFilter(e.target.value as LanguageTag | "")}
          className="rounded border border-surface-alt px-3 py-2 text-sm"
        >
          <option value="">All languages</option>
          <option value="en">English</option>
          <option value="vi">Vietnamese</option>
          <option value="km">Khmer</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "" | "active" | "inactive")}
          aria-label="Filter by status"
          className="rounded border border-surface-alt px-3 py-2 text-sm"
        >
          <option value="">Active and deactivated</option>
          <option value="active">Active only</option>
          <option value="inactive">Deactivated only</option>
        </select>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as "" | "feedback" | "scored")}
          aria-label="Filter by kind"
          className="rounded border border-surface-alt px-3 py-2 text-sm"
        >
          <option value="">All questions</option>
          <option value="scored">Scored only</option>
          <option value="feedback">Feedback only</option>
        </select>
      </div>

      {error && (
        <p role="alert" className="mb-4 rounded border border-accent bg-surface-alt px-3 py-2 text-sm text-accent">
          {error}
        </p>
      )}

      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-surface-alt text-body">
            <th className="py-2">Text</th>
            <th>Type</th>
            <th>Language</th>
            <th>App tags</th>
            <th>Visibility</th>
            <th>Deactivate</th>
            <th>Remove</th>
            <th>Edit</th>
          </tr>
        </thead>
        <tbody>
          {visibleQuestions.map((q) => (
            <tr
              key={q.id}
              className={`border-b border-surface-alt ${q.status === "inactive" ? "opacity-60" : ""}`}
            >
              <td className="max-w-xs truncate py-2 text-ink">{q.text}</td>
              <td>{q.type}</td>
              <td>{q.language}</td>
              <td>
                {q.isFeedback ? (
                  <span className="rounded bg-brand/10 px-2 py-0.5 text-xs text-brand">
                    {t("questionForm.feedbackBadge", "Feedback")}
                  </span>
                ) : (
                  q.appTags.join(", ")
                )}
                {q.targetPositionTypes.length > 0 && (
                  <span className="ml-2 rounded bg-surface-alt px-2 py-0.5 text-xs text-body">
                    {q.targetPositionTypes.join(", ")}
                  </span>
                )}
              </td>
              <td>{q.visibility === "company_specific" ? "Company-specific" : "Global"}</td>
              <td className="py-2">
                {/* The switch reads as "is this question deactivated?", matching the column
                    header. The word beside it spells the resulting state out, because a bare
                    switch under a verb heading is genuinely ambiguous. */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={q.status === "inactive"}
                    aria-label={`Deactivate: ${q.text}`}
                    disabled={busyId === q.id}
                    onClick={() => handleToggleActive(q)}
                    className={`relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                      q.status === "inactive" ? "bg-accent" : "bg-surface-alt"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-surface shadow transition-all ${
                        q.status === "inactive" ? "left-[1.125rem]" : "left-0.5"
                      }`}
                    />
                  </button>
                  <span className="text-xs text-body">
                    {q.status === "inactive" ? "Deactivated" : "Active"}
                  </span>
                </div>
              </td>
              <td className="py-2">
                <button
                  onClick={() => handleRemove(q)}
                  disabled={busyId === q.id}
                  className="text-accent disabled:opacity-50"
                >
                  Remove
                </button>
              </td>
              <td className="py-2">
                <button onClick={() => setEditing(q)} className="text-brand">
                  Edit
                </button>
              </td>
            </tr>
          ))}
          {visibleQuestions.length === 0 && (
            <tr>
              <td colSpan={8} className="py-6 text-center text-body">
                No questions match these filters yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
